import React, { useState, useEffect, useCallback } from 'react';
import {
  getSocietyAdminData,
  getScheduledBills,
  ScheduleBill,
} from '../services/api';
import { HiExclamation, HiRefresh } from '../components/Icons';
import './SocietyAdminScheduledBills.css';

const formatCurrency = (amount: number, perSqft?: boolean): string => {
  const base =
    '\u20B9' +
    amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return perSqft ? `${base} / sq.ft` : base;
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = iso.slice(0, 10);
  const [y, m, day] = d.split('-');
  if (!y || !m || !day) return iso;
  return `${day}/${m}/${y}`;
};

const frequencyLabel = (bill: ScheduleBill): string => {
  const freq = bill.frequency?.toLowerCase() ?? 'monthly';
  if (freq === 'weekly' && bill.days_of_week) {
    return `Weekly (${bill.days_of_week})`;
  }
  if (bill.day_of_month) {
    return `${freq} · day ${bill.day_of_month}`;
  }
  if (bill.interval_value > 1) {
    return `Every ${bill.interval_value} ${freq}`;
  }
  return freq;
};

const SocietyAdminScheduledBills: React.FC = () => {
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId ?? 0;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bills, setBills] = useState<ScheduleBill[]>([]);

  const load = useCallback(async () => {
    if (!societyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await getScheduledBills(societyId);
      setBills(res.data ?? []);
    } catch (err: any) {
      setError(err.message || 'Failed to load scheduled bills');
    } finally {
      setLoading(false);
    }
  }, [societyId]);

  useEffect(() => {
    if (!societyId) {
      setLoading(false);
      setError('No society selected.');
      return;
    }
    void load();
  }, [societyId, load]);

  if (!societyId) {
    return (
      <div className="sasb-page">
        <div className="recon-alert recon-alert-error">
          <HiExclamation size={18} /> No society selected.
        </div>
      </div>
    );
  }

  return (
    <div className="sasb-page">
      <div className="sasb-header">
        <div>
          <h2>Scheduled bills</h2>
          <p className="sasb-subtitle">
            Recurring bill templates for this society. Create or edit schedules in the TappyTray
            mobile app.
          </p>
        </div>
        <button
          type="button"
          className="recon-btn recon-btn-outline"
          onClick={() => void load()}
          disabled={loading}
        >
          <HiRefresh size={16} /> Refresh
        </button>
      </div>

      {error && (
        <div className="recon-alert recon-alert-error">
          <HiExclamation size={18} /> {error}
        </div>
      )}

      {loading ? (
        <div className="sasb-loading">
          <HiRefresh size={28} className="spin" />
          <p>Loading scheduled bills…</p>
        </div>
      ) : bills.length === 0 ? (
        <div className="sasb-empty">
          <p>No scheduled bills configured for this society.</p>
          <p className="sasb-empty-hint">
            Use the mobile app to set up recurring maintenance or other auto-billing templates.
          </p>
        </div>
      ) : (
        <div className="sasb-table-wrap">
          <table className="sasb-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Amount</th>
                <th>Schedule</th>
                <th>Start</th>
                <th>End</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {bills.map((bill) => (
                <tr key={bill.id}>
                  <td>
                    <div className="sasb-title">{bill.title}</div>
                    {bill.description && (
                      <div className="sasb-desc">{bill.description}</div>
                    )}
                  </td>
                  <td>{bill.bill_type}</td>
                  <td>{formatCurrency(bill.amount, bill.amount_per_sqft)}</td>
                  <td>{frequencyLabel(bill)}</td>
                  <td>{formatDate(bill.start_date)}</td>
                  <td>{formatDate(bill.end_date)}</td>
                  <td>
                    <span
                      className={`recon-badge ${bill.is_active ? 'badge-green' : 'badge-yellow'}`}
                    >
                      {bill.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {bill.applies_to_occupied_only && (
                      <span className="sasb-tag">Occupied only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SocietyAdminScheduledBills;
